import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { affiliatePublicApi } from "../lib/affiliateApi";

const maskPhone = (v) => {
    const d = String(v || "").replace(/\D/g, "").slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 6) return `(${d.slice(0,2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
    return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
};
const maskCpf = (v) => {
    const d = String(v || "").replace(/\D/g, "").slice(0, 11);
    return d.replace(/^(\d{3})(\d)/, "$1.$2").replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
};

/**
 * AffiliateContractModal — mostra o conteúdo atual do Contrato de Afiliados
 * (endpoint público). O botão de aceite só habilita quando o afiliado rola até
 * o fim (ou quando o texto é curto demais pra rolar).
 */
const AffiliateContractModal = ({ onClose, onReadToEnd }) => {
    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [atEnd, setAtEnd] = useState(false);
    const bodyRef = useRef(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const doc = await affiliatePublicApi.contract();
                if (!alive) return;
                setContract(doc);
            } catch (e) {
                if (alive) setError(e?.message || "Não foi possível carregar o contrato.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const markEnd = useCallback(() => {
        setAtEnd(true);
        onReadToEnd?.();
    }, [onReadToEnd]);

    // Texto curto que não gera rolagem → libera assim que renderiza.
    const checkNoScroll = useCallback((node) => {
        bodyRef.current = node;
        if (node && node.scrollHeight <= node.clientHeight + 4) markEnd();
    }, [markEnd]);

    const onScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) markEnd();
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return createPortal(
        <div
            data-testid="affiliate-contract-modal"
            onClick={onClose}
            className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg rounded-3xl border border-amber-500/20 bg-slate-950 shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
            >
                <div className="p-5 border-b border-white/5 flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Programa de Afiliados</div>
                        <h3 className="salon-heading text-xl text-white mt-1">
                            Contrato de Afiliados{contract?.version ? ` · v${contract.version}` : ""}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Fechar"
                        className="h-8 w-8 rounded-lg border border-white/10 text-slate-400 hover:text-white flex items-center justify-center shrink-0"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>

                <div
                    ref={checkNoScroll}
                    onScroll={onScroll}
                    data-testid="affiliate-contract-modal-body"
                    className="p-5 overflow-y-auto text-sm text-slate-300 whitespace-pre-wrap leading-relaxed flex-1"
                >
                    {loading ? "Carregando…" : error ? <span className="text-red-400">{error}</span> : (contract?.content || "Contrato ainda não publicado.")}
                </div>

                <div className="p-5 border-t border-white/5">
                    {!atEnd && !loading && !error && (
                        <div className="text-[11px] text-slate-500 mb-2 text-center">
                            <i className="fa-solid fa-arrow-down mr-1"></i>Role até o final para continuar
                        </div>
                    )}
                    <button
                        data-testid="affiliate-contract-modal-accept"
                        onClick={onClose}
                        disabled={!atEnd}
                        className="w-full min-h-[48px] rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold uppercase tracking-[0.15em] text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Li o contrato
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const AffiliateSignup = () => {
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [form, setForm] = useState({
        name: "", email: "", phone: "", cpf: "", pix_key: "", password: "", password_confirm: "", terms_accepted: false,
    });
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    // Contrato de Afiliados: o checkbox "Li e aceito" só habilita depois que o
    // afiliado abre o modal e rola até o final.
    const [contractModalOpen, setContractModalOpen] = useState(false);
    const [contractRead, setContractRead] = useState(false);

    // Captura ref_code (afiliado que indica outro afiliado)
    const referredBy = useMemo(() => {
        try {
            const sp = new URLSearchParams(window.location.search);
            const fromUrl = sp.get("ref");
            if (fromUrl) return fromUrl;
            const cookie = document.cookie.split(";").find((c) => c.trim().startsWith("kinng_ref="));
            if (cookie) return decodeURIComponent(cookie.split("=")[1] || "");
            return localStorage.getItem("kinng_ref") || null;
        } catch { return null; }
    }, []);

    const canSubmit = useMemo(() => {
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
        return form.name.trim().length >= 2 && emailOk
            && form.phone.replace(/\D/g,"").length >= 10
            && form.cpf.replace(/\D/g,"").length === 11
            && form.password.length >= 8
            && form.password === form.password_confirm
            && form.terms_accepted;
    }, [form]);

    const submit = async () => {
        setSaving(true);
        try {
            await affiliatePublicApi.register({
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone,
                cpf: form.cpf,
                pix_key: form.pix_key || form.cpf,
                password: form.password,
                terms_accepted: true,
                ref_code: referredBy || null,
            });
            setSubmitted(true);
        } catch (e) {
            toast.error("Erro no cadastro", { description: e.message });
        } finally { setSaving(false); }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center px-6 py-10">
                <div className="max-w-lg w-full text-center rounded-3xl border border-amber-500/30 bg-amber-500/5 p-8" data-testid="affiliate-signup-success">
                    <div className="h-16 w-16 mx-auto rounded-full bg-amber-500/15 border border-amber-400/40 flex items-center justify-center mb-4">
                        <i className="fa-solid fa-hourglass text-3xl text-amber-300"></i>
                    </div>
                    <h1 className="salon-heading text-2xl text-white mb-2">Solicitação enviada!</h1>
                    <p className="text-sm text-slate-400 mb-5">
                        Sua conta de afiliado está aguardando aprovação da equipe Kinng.app.
                        Você receberá um WhatsApp assim que for aprovada com seu link único de indicação.
                    </p>
                    <Link to="/afiliado" className="inline-block rounded-xl border border-white/10 hover:border-amber-500/40 text-slate-300 hover:text-amber-200 px-5 py-2.5 text-sm transition-all">
                        Ir para o portal
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="affiliate-signup-page">
            <header className="border-b border-white/5">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Programa de Afiliados</div>
                        <div className="salon-heading text-lg text-white">Quero ser afiliado Kinng.app</div>
                    </div>
                    <Link to="/afiliado" className="text-xs text-slate-400 hover:text-amber-200" data-testid="affiliate-signup-back">
                        Já sou afiliado<i className="fa-solid fa-arrow-right ml-1"></i>
                    </Link>
                </div>
                {referredBy && (
                    <div className="max-w-3xl mx-auto px-6 pb-3">
                        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-xs text-emerald-200 flex items-center gap-2" data-testid="affiliate-signup-referred-banner">
                            <i className="fa-solid fa-user-group"></i>
                            Você foi convidado por <b className="font-mono">{referredBy}</b>.
                        </div>
                    </div>
                )}
            </header>

            <main className="max-w-2xl mx-auto px-6 py-8 pb-32 space-y-4">
                <div className="mb-3">
                    <h2 className="salon-heading text-3xl text-white mb-1">Ganhe indicando o Kinng.app</h2>
                    <p className="text-sm text-slate-500">Preencha seus dados e comece a lucrar com cada salão que se cadastrar pelo seu link único.</p>
                </div>

                {[
                    ["name", "Nome completo *", "text", "Como devemos te chamar?"],
                    ["email", "E-mail (login do portal) *", "email", "voce@email.com"],
                ].map(([k, l, t, ph]) => (
                    <div key={k}>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">{l}</div>
                        <input data-testid={`affiliate-signup-input-${k}`} type={t} value={form[k]}
                            onChange={(e) => set({ [k]: e.target.value })}
                            placeholder={ph}
                            className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-500/40" />
                    </div>
                ))}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Telefone *</div>
                        <input data-testid="affiliate-signup-input-phone" value={form.phone}
                            onChange={(e) => set({ phone: maskPhone(e.target.value) })}
                            placeholder="(11) 91234-5678"
                            className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-amber-500/40" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">CPF *</div>
                        <input data-testid="affiliate-signup-input-cpf" value={form.cpf}
                            onChange={(e) => set({ cpf: maskCpf(e.target.value), pix_key: form.pix_key || maskCpf(e.target.value) })}
                            placeholder="000.000.000-00"
                            className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-amber-500/40" />
                    </div>
                </div>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Chave PIX (default: CPF)</div>
                    <input data-testid="affiliate-signup-input-pix" value={form.pix_key}
                        onChange={(e) => set({ pix_key: e.target.value })}
                        placeholder="CPF, e-mail, telefone ou chave aleatória"
                        className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-500/40" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Senha *</div>
                        <div className="relative">
                            <input data-testid="affiliate-signup-input-password" type={showPassword ? "text" : "password"} value={form.password}
                                onChange={(e) => set({ password: e.target.value })}
                                placeholder="mín. 8 caracteres"
                                className="w-full bg-slate-900/60 border border-white/10 rounded-xl pl-4 pr-11 py-3 text-sm outline-none focus:border-amber-500/40" />
                            <button
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-500 hover:text-amber-200"
                                data-testid="affiliate-signup-password-toggle"
                            >
                                <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"} text-sm`}></i>
                            </button>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Confirmar senha *</div>
                        <div className="relative">
                            <input data-testid="affiliate-signup-input-password-confirm" type={showPasswordConfirm ? "text" : "password"} value={form.password_confirm}
                                onChange={(e) => set({ password_confirm: e.target.value })}
                                placeholder="repita a senha"
                                className="w-full bg-slate-900/60 border border-white/10 rounded-xl pl-4 pr-11 py-3 text-sm outline-none focus:border-amber-500/40" />
                            <button
                                type="button"
                                onClick={() => setShowPasswordConfirm((v) => !v)}
                                aria-label={showPasswordConfirm ? "Ocultar senha" : "Mostrar senha"}
                                className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-500 hover:text-amber-200"
                                data-testid="affiliate-signup-password-confirm-toggle"
                            >
                                <i className={`fa-solid ${showPasswordConfirm ? "fa-eye-slash" : "fa-eye"} text-sm`}></i>
                            </button>
                        </div>
                    </div>
                </div>
                {form.password && form.password_confirm && form.password !== form.password_confirm && (
                    <div className="text-xs text-red-400">As senhas não conferem.</div>
                )}

                <label className="flex items-start gap-3 pt-2" data-testid="affiliate-signup-terms">
                    <input type="checkbox" checked={form.terms_accepted}
                        disabled={!contractRead}
                        onChange={(e) => set({ terms_accepted: e.target.checked })}
                        className="mt-1 h-4 w-4 accent-amber-500 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="affiliate-signup-terms-checkbox" />
                    <span className="text-xs text-slate-400">
                        Li e aceito o{" "}
                        <button
                            type="button"
                            data-testid="affiliate-signup-open-contract"
                            onClick={() => setContractModalOpen(true)}
                            className="text-amber-300 underline"
                        >
                            Contrato do Programa de Afiliados
                        </button>{" "}
                        do Kinng.app.
                        {!contractRead && (
                            <span className="block text-[11px] text-slate-500 mt-0.5">
                                Abra o contrato e role até o final para liberar o aceite.
                            </span>
                        )}
                    </span>
                </label>
            </main>

            {contractModalOpen && (
                <AffiliateContractModal
                    onClose={() => setContractModalOpen(false)}
                    onReadToEnd={() => setContractRead(true)}
                />
            )}

            <footer className="fixed bottom-0 inset-x-0 border-t border-white/5 bg-slate-950/95 backdrop-blur-xl">
                <div className="max-w-2xl mx-auto px-6 py-4 flex justify-end">
                    <button data-testid="affiliate-signup-submit" onClick={submit} disabled={!canSubmit || saving}
                        className="rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-6 py-3 text-sm disabled:opacity-40 transition-all">
                        {saving ? (
                            <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Enviando…</>
                        ) : (
                            <>Quero ser afiliado<i className="fa-solid fa-arrow-right ml-2"></i></>
                        )}
                    </button>
                </div>
            </footer>
        </div>
    );
};

export default AffiliateSignup;
